import React from "react";
import { PenLine, Search, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Localizer } from "@/main/react/Types";
import SearchZeroMatchShelf, { type SearchZeroMatchShelfOptions } from "@/main/react/page/search/SearchZeroMatchShelf";

/**
 * The two actions offered when a PEOPLE search comes back empty (design §3.2).
 *
 * Present only when the caller has established all three of: the active tab is users, the result set is
 * empty, and a session exists. This component does not decide any of that — a logged-out visitor has no
 * profile to share and no invite endpoint to reach, so rendering either action for them would be a
 * button that 401s, and the page that already knows the session is the right place to make that call.
 */
export interface UnresolvedPeopleSearchActions {
    /** Shares the VIEWER's own public profile — never the searched-for stranger's. */
    onShareProfile: () => void;
    /** Opens Phase C's invite form with the email field empty. */
    onInvite: () => void;
}

/**
 * Everything a zero-match search page may offer the person whose query just failed, in one block that
 * BOTH zero-match renderings can mount (`EMPTY-STATES-67`).
 *
 * <p><b>Why this is its own export.</b> The identical zero produced two different products depending
 * on which tab you landed on. Thoughts and Users do not blend, so their list settles empty and
 * `SearchPaginatedList` reaches `SearchEmptyState`, which carried the recovery actions. Posts, Groups
 * and Series blend six filler rows, so `visibleItems.length === 0` is never true there and the whole
 * page held no control acting on the failed query — one grey sentence and six unrelated cards. Lifting
 * the actions out of the panel is what lets the blending branch take them too, which is exactly what
 * `EMPTY-STATES-67`'s Direction asked for.
 *
 * <p><b>It renders on the blending branch OUTSIDE the live region, never inside it.</b>
 * `SearchBlendDivider`'s zero verdict is mounted inside an always-mounted `role="status"` region
 * (`DISCOVERY-38`); interactive controls inside a live region become part of every announcement. The
 * mount point in `SearchPaginatedList` is the region's next sibling for that reason.
 *
 * <p><b>The authorship prompt is copy that already shipped</b> (`DISCOVERY-19`, `EMPTY-STATES-103`,
 * D20). `search.no_results.write_it.description` / `.action` were translated into all 25 catalogs and
 * had no call site anywhere in the repo. The action carries the failed query in its own label ("Write
 * about {0}") — a generic compose button is the thing `DISCOVERY-19` criticises, so the relationship to
 * the query has to be visible on the control itself, not only in what it opens. The `.title` sibling
 * was deleted in `DISCOVERY-61`; see the comment on the block itself.
 *
 * <p>The prompt's lead line is a `<p>`, not a heading, on purpose. This block mounts on two surfaces with different
 * heading contexts — inside `SearchEmptyState`, which already owns the panel's `h2`, and bare under the
 * page `h1` on the blending branch — so any single level is wrong on one of them, and `DISCOVERY-9` is
 * the record of what a mis-levelled heading costs here. Rank comes from weight, as it does for the
 * verdict above it (`EMPTY-STATES-104`).
 *
 * <p>Renders `null` when there is nothing to offer, rather than an empty row: `onClearFilters` arrives
 * only when a refinement exists to clear, and `onTryShorterQuery` only when a shorter query exists.
 */
export function SearchZeroMatchActions(props: {
    query: string;
    localizer: Localizer;
    onClearFilters?: () => void;
    onTryShorterQuery?: () => void;
    /**
     * Opens the composer seeded with the failed query. Omitted by the caller for the `users` vertical
     * ("write about @handle" is not the failure that vertical had) and whenever there is no query.
     */
    onWriteAboutQuery?: () => void;
    /** `center` inside the bordered panel; `left` under the blend verdict, which is left-aligned. */
    align?: "left" | "center";
    className?: string;
}) {
    const { query, localizer, onClearFilters, onTryShorterQuery, onWriteAboutQuery, className } = props;
    const align = props.align ?? "center";
    const centered = align === "center";

    const trimmedQuery = query.trim();
    const showWriteIt = Boolean(onWriteAboutQuery) && trimmedQuery.length > 0;
    const showRecoveryActions = Boolean(onClearFilters || onTryShorterQuery);

    if (!showWriteIt && !showRecoveryActions) {
        return null;
    }

    return (
        <div
            className={cn("space-y-5", centered ? "text-center" : "text-left", className)}
            data-playwright-id="search-zero-match-actions"
        >
            {/* `DISCOVERY-53` — the coarse-pointer floor, on all three controls in this component.
              *
              * Measured at 412 px the primary CTA rendered 178 × 36.2 CSS px, exactly `Button`'s
              * `size: default` (`h-9`), against the 44 px floor `css/03-tap-targets.css` calls the
              * canonical rule. None of the three went through a sanctioned path to it: `buttonVariants`
              * gives the coarse floor to `size="icon-touch"` ONLY, so being on the shared `Button` does
              * NOT inherit it — the same trap `ERRORS-17` hit on the tag hub, recorded in `TagPage.tsx`.
              *
              * `[@media(pointer:coarse)]:min-h-11` rather than `touch-hit-44`, and for the reason the
              * tag hub gives: these rows WRAP at 412 px in a verbose locale, and a centred 44 px slug on
              * a 36 px control overhangs 4 px each side and would collide across the `gap-3`. A floor on
              * the box itself makes the pitch 44 + 12 by construction.
              *
              * Gated on `(pointer: coarse)`, never on `sm:` — the floor is a property of the input
              * device, and `scripts/check-tap-target-mechanisms.mjs` check C fails a breakpoint form.
              * Desktop is unchanged; these still render 36 px on a fine pointer. */}
            {showRecoveryActions ? (
                <div className={cn("flex flex-wrap items-center gap-3", centered ? "justify-center" : "justify-start")}>
                    {onClearFilters ? (
                        <Button
                            type="button"
                            variant="outline"
                            className="[@media(pointer:coarse)]:min-h-11"
                            onClick={onClearFilters}
                        >
                            {localizer("search.clear_filters")}
                        </Button>
                    ) : null}
                    {onTryShorterQuery ? (
                        <Button
                            type="button"
                            className="[@media(pointer:coarse)]:min-h-11"
                            onClick={onTryShorterQuery}
                        >
                            {localizer("search.try_shorter_query")}
                        </Button>
                    ) : null}
                </div>
            ) : null}

            {showWriteIt ? (
                <div className="space-y-1" data-playwright-id="search-write-it-prompt">
                    {/* `DISCOVERY-61` — ONE line here, and it is not a second title.
                      *
                      * This block used to open with `search.no_results.write_it.title` ("Nobody has
                      * written about this yet") in `text-base font-semibold leading-6 text-foreground`
                      * — a class string BYTE-IDENTICAL to the verdict `SearchBlendDivider` renders four
                      * lines above, in a different file. So the page presented two peer titles of equal
                      * rank with nothing saying which one led, and between them the same fact was
                      * stated four times before a phone showed anything actionable (the prose ran CSS
                      * y 256->439 of an 839 px viewport and pushed the first card to 510).
                      *
                      * The reduction is a deletion, not a rewrite. The verdict belongs to the block
                      * above; what belongs here is the OFFER, so only the invitation survives and it is
                      * subordinate by weight (`text-sm font-medium`, not `text-base font-semibold`).
                      * `search.no_results.write_it.title` had no other consumer and was deleted from
                      * all 25 catalogs rather than left dead; `.description` now carries the invitation
                      * alone ("You could be the one who writes it."), its first sentence — a third
                      * restatement of the absence — removed in every catalog. */}
                    <p className="text-sm font-medium leading-6 text-foreground">
                        {localizer("search.no_results.write_it.description")}
                    </p>
                    <div className={cn("flex flex-wrap pt-2", centered ? "justify-center" : "justify-start")}>
                        {/* `DISCOVERY-72` — SECONDARY, by operator decision (wave-19 re-adjudication
                          * §1.1, Q1 answer A: *"Write-a-post drops to secondary"*).
                          *
                          * This was a filled primary from `DISCOVERY-19`, when it was the only control
                          * on the page acting on the failed query. It no longer is: the shelf above it
                          * carries the answer that is both on-subject and free, and for an anonymous
                          * stranger — who is most of this page's traffic — this control's continuation
                          * is a signup wall. A filled primary above a login gate, ranked over a set of
                          * links that cost nothing, is the emphasis the adjudication reversed.
                          *
                          * `variant="outline"` is a PRESENTATION change and nothing else (rule 23): the
                          * offer, its copy, its handler and which verticals may show it are all
                          * unchanged, and it still names the query on the control itself. */}
                        <Button
                            type="button"
                            variant="outline"
                            // `max-w-full` stays: the label interpolates an unbounded query, and the
                            // floor must not let the control grow horizontally past its container
                            // (`DISCOVERY-53`'s one constraint). A min-height cannot widen it.
                            className="max-w-full [@media(pointer:coarse)]:min-h-11"
                            onClick={onWriteAboutQuery}
                            data-playwright-id="search-write-about-query"
                        >
                            <PenLine className="mr-2 size-4 shrink-0" aria-hidden="true" />
                            {/* A search query has no length limit worth trusting, and this label
                                interpolates it. `truncate` keeps a long query from stretching the
                                control past its container on a phone; the full query is still in the
                                field and in the address. */}
                            <span className="truncate">
                                {localizer("search.no_results.write_it.action", trimmedQuery)}
                            </span>
                        </Button>
                    </div>
                </div>
            ) : null}
        </div>
    );
}

export function SearchEmptyState(props: {
    query: string;
    localizer: Localizer;
    onClearFilters?: () => void;
    onTryShorterQuery?: () => void;
    /** Forwarded verbatim to {@link SearchZeroMatchActions}; see its docs. */
    onWriteAboutQuery?: () => void;
    unresolvedPeopleSearch?: UnresolvedPeopleSearchActions;
    /**
     * True when EVERY vertical came back with a zero count, not just this one (`EMPTY-STATES-11`).
     *
     * Three of the five verticals blend filler and two do not, so one zero-match query used to produce
     * twenty confident result cards on Posts/Groups/Series and this panel on Thoughts/Users. Its body
     * copy then said "check the other tabs" — a dead instruction on exactly that state, and worse than
     * dead: a reader who follows it lands on twenty cards and can only conclude those tabs did have
     * matches after all. Blending the other two is not the answer (neither has a filler source, and
     * "people you might like" for a person search is worse than an honest empty), so the two renderings
     * converge on the COPY.
     */
    allVerticalsEmpty?: boolean;
    /**
     * Present ⇒ mount the typeahead's topic/group shelf above the actions (`DISCOVERY-72`).
     *
     * Absent ⇒ nothing, which is what every hub gets: a hub supplies its own `emptyState`, and the two
     * that fall through to this panel pass no query, so a shelf there would be built from nothing.
     * `/search` supplies it per vertical.
     */
    zeroMatchShelf?: SearchZeroMatchShelfOptions;
}) {
    const { query, localizer, onClearFilters, onTryShorterQuery, unresolvedPeopleSearch } = props;

    return (
        <div className="rounded-xl border border-border bg-card px-6 py-8 text-center">
            <div className="mx-auto flex size-14 items-center justify-center rounded-full bg-background2/80 text-muted-foreground">
                <Search className="size-6" aria-hidden="true" />
            </div>

            {/* The HEADING branches on the query for the same reason the body below already does:
                with no query nothing was searched, so "No results" reports the failure of a search
                that never ran.

                `h2`, not `h3` (`DISCOVERY-9`). This panel is the direct child of a page whose only
                other heading is its `h1`, on both surfaces that render it — `/search` and every hub —
                so `h3` skipped a rung and left screen-reader heading navigation with a gap where the
                page's structure should be. A caller-set `headingLevel` prop was the register's
                suggestion and is deliberately not taken: after the `/search` `h1` landed, every call
                site sits at the same depth, and a prop nobody passes is a second way to be wrong. */}
            <h2 className="mt-4 text-lg font-semibold text-foreground">
                {query.trim() ? localizer("search.no_results") : localizer("search.summary.default")}
            </h2>
            <p
                className="mt-2 text-sm leading-6 text-muted-foreground"
                data-playwright-id="search-empty-description"
            >
                {/*
                  * This used to render `search.summary.results_for` ("Search results for “x”") — a
                  * page-TITLE string used as the explanatory paragraph, which read like a loading
                  * state that had failed rather than an answer. The query itself is already visible
                  * in the search box and page header, so the body's job is to say what to do next.
                  *
                  * The all-zero branch drops the "check the other tabs" pointer; see the prop's docs.
                  */}
                {query.trim()
                    ? localizer(props.allVerticalsEmpty
                        ? "search.no_results.description_everywhere"
                        : "search.no_results.description")
                    : localizer("paginated.empty.description")}
            </p>

            {/*
              * Share BEFORE invite, and that order is not cosmetic (§3.2). The share is instant and
              * costs the other person nothing; the invite asks for someone's email address. They serve
              * the two halves of the same failure — the person is here under a handle you mis-spelled,
              * or they are genuinely not here — and which half you are in is not knowable from the query.
              */}
            {unresolvedPeopleSearch ? (
                <>
                {/* Round 38 D24 (EMPTY-STATES-117): the two actions below refer to someone the member
                    looked for and did not find, so one line says so before them. */}
                <p className="mt-5 text-sm leading-6 text-foreground" data-playwright-id="search-people-zero-bridge">
                    {localizer("search.people.zero.bridge")}
                </p>
                <div className="mt-3 flex flex-wrap items-center justify-center gap-3">
                    <Button type="button" onClick={unresolvedPeopleSearch.onShareProfile}>
                        <Sparkles className="mr-2 size-4" aria-hidden="true" />
                        {localizer("search.unresolved_people.share_profile")}
                    </Button>
                    <Button type="button" variant="outline" onClick={unresolvedPeopleSearch.onInvite}>
                        {localizer("search.unresolved_people.invite")}
                    </Button>
                </div>
                </>
            ) : null}

            {/* `DISCOVERY-72` — the shelf sits BETWEEN the verdict above and the actions below, which is
                the order the wave-19 re-adjudication §1.1 adjudicated: verdict → shelf → actions. The
                panel's own heading and body are the verdict here; on the blending branch it is
                `SearchBlendDivider`, and `SearchPaginatedList` mounts the same component in the same
                slot so the two renderings of the identical zero stay one product. It renders `null`
                whenever the suggestion response has not resolved anything for this exact query, so an
                unanswerable query leaves the panel exactly as it was. */}
            {props.zeroMatchShelf ? (
                <SearchZeroMatchShelf
                    className="mt-5 text-left"
                    query={query}
                    localizer={localizer}
                    options={props.zeroMatchShelf}
                />
            ) : null}

            {/* `EMPTY-STATES-67` — these controls used to be written out here and here only, which is
                why the three blending verticals had none. They now live in `SearchZeroMatchActions`
                above, which `SearchPaginatedList` also mounts on the blending branch. The wrapper's
                own `mt-5` disappears with it when there is nothing to offer, so an empty panel no
                longer carries 20px of reserved space for controls that never render. */}
            <SearchZeroMatchActions
                className="mt-5"
                query={query}
                localizer={localizer}
                onClearFilters={onClearFilters}
                onTryShorterQuery={onTryShorterQuery}
                onWriteAboutQuery={props.onWriteAboutQuery}
                align="center"
            />
        </div>
    );
}

export default SearchEmptyState;
